import { Checkbox, Tooltip, useCheckboxState } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useWatchAlias } from 'hooks'
import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useFormStateContext } from '../../../../providers'
import { FormAliasKeys } from '../../../../providers/useFormAlias.constate'
import { messages } from './messages'

interface Props {
  name: string
}

export function AccumulateManualPrices(props: Props) {
  const { name } = props

  const { $t } = useIntl()

  const { form, readOnly } = useFormStateContext()

  const accumulateWithManualPrices: boolean | null = useWatch({ form, name })

  const state = useCheckboxState({ initialValue: !!accumulateWithManualPrices })

  const benefitType = useWatchAlias<string>({
    key: FormAliasKeys.benefitType,
    form,
  })

  const benefitIsCompatible = useMemo(() => {
    return benefitType
      ? benefitType === 'nominal' || benefitType === 'percentual'
      : true
  }, [benefitType])

  const benefitIsCompatibleBackup = useRef<boolean | undefined>()

  /**
   * Manages form value synchronization based on benefit type compatibility.
   *
   * The backend expects:
   * - boolean (true/false) when the benefit type is compatible
   * - null when the benefit type is incompatible
   *
   * The checkbox state always uses boolean values for UI consistency.
   *
   * Logic:
   * - On first load: Set form value to null if benefit is incompatible
   * - On benefit type changes:
   *   - If becoming compatible: Reset form value to false and checkbox to unchecked
   *   - If becoming incompatible: Set form value to null and checkbox to unchecked
   */
  useEffect(() => {
    const firstLoad = benefitIsCompatibleBackup.current === undefined
    if (firstLoad) {
      !benefitIsCompatible && form.setValue(name, null)
    } else {
      const benefitHasChanged =
        benefitIsCompatibleBackup.current !== benefitIsCompatible
      if (benefitHasChanged) {
        if (benefitIsCompatible) {
          form.setValue(name, false)
        } else {
          form.setValue(name, null)
        }
        state.setValue(false)
      }
    }
    benefitIsCompatibleBackup.current = benefitIsCompatible
  }, [benefitIsCompatible, form, name, state])

  return (
    <Tooltip
      text={$t(messages.accumulateWithManualPrices.tooltip)}
      placement="top"
      visible={benefitIsCompatible ? false : undefined}
    >
      <div data-testid="accumulate-with-manual-prices-container">
        <Checkbox
          state={state}
          disabled={readOnly || !benefitIsCompatible}
          label={$t(messages.accumulateWithManualPrices.label)}
          onChange={({ target: { checked } }) => {
            form.setValue(name, checked)
          }}
        />
      </div>
    </Tooltip>
  )
}
